/**
 * @file     SocialAuthButtons.tsx
 * @purpose  "Continue with Google" and "Continue with Apple" above an email
 *           form, then an "or with email" divider. Each provider renders only
 *           when its feature flag is on, and the block renders nothing when
 *           both are off. Colours, marks and wording follow the Google and
 *           Apple sign-in brand guidelines, so they are fixed per provider
 *           rather than drawn from the site palette.
 */
'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useTranslation } from 'react-i18next';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { motionTokens } from '@/design-system/tokens';
import { enabledOAuthProviders, type OAuthProvider } from '@/services/auth/oauthProviders';
import { startOAuthSignIn } from '@/services/auth/startOAuthSignIn';
import { gateColors } from './GateSheet';

const DARK = 'html[data-theme="dark"] &';

// Provider-mandated button colours (Google "Sign in with Google" light and dark
// themes; Apple "Sign in with Apple" black and white styles).
const brand = {
  google: {
    light: { bg: '#FFFFFF', border: '#747775', text: '#1F1F1F' }, // lint-allow-raw-color: Google brand guidelines
    dark: { bg: '#131314', border: '#8E918F', text: '#E3E3E3' }, // lint-allow-raw-color: Google brand guidelines
  },
  apple: {
    light: { bg: '#000000', border: '#000000', text: '#FFFFFF' }, // lint-allow-raw-color: Apple brand guidelines
    dark: { bg: '#FFFFFF', border: '#FFFFFF', text: '#000000' }, // lint-allow-raw-color: Apple brand guidelines
  },
} as const;

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />{/* lint-allow-raw-color: Google G mark */}
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18z" />{/* lint-allow-raw-color: Google G mark */}
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34z" />{/* lint-allow-raw-color: Google G mark */}
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z" />{/* lint-allow-raw-color: Google G mark */}
    </svg>
  );
}

function AppleMark() {
  return (
    <svg width="17" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

export interface SocialAuthButtonsProps {
  /** Same-origin path to land on after the provider returns. */
  next?: string | null;
  /** Runs right before the browser leaves for the provider (e.g. stash flow state). */
  onBeforeRedirect?: (provider: OAuthProvider) => void;
  disabled?: boolean;
  /** Hide the "or with email" divider when nothing follows the buttons. */
  divider?: boolean;
}

export function SocialAuthButtons({ next, onBeforeRedirect, disabled = false, divider = true }: SocialAuthButtonsProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Coming back with the browser Back button restores this page from the
  // back-forward cache with the button still pending; release it.
  useEffect(() => {
    const onShow = (event: PageTransitionEvent) => { if (event.persisted) setPending(null); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);
  const providers = enabledOAuthProviders();
  if (providers.length === 0) return null;

  const start = async (provider: OAuthProvider) => {
    setError(null);
    setPending(provider);
    onBeforeRedirect?.(provider);
    const { error: failure } = await startOAuthSignIn(provider, next ?? (typeof window !== 'undefined' ? window.location.pathname + window.location.search : null));
    // No error means Supabase has started the redirect: stay pending while
    // the browser leaves. Only a real failure shows the message.
    if (!failure) return;
    setPending(null);
    setError(t('auth.social.failed', { defaultValue: 'That did not go through. Try again.' }));
  };


  const label = (provider: OAuthProvider) =>
    provider === 'google'
      ? t('auth.social.google', { defaultValue: 'Continue with Google' })
      : t('auth.social.apple', { defaultValue: 'Continue with Apple' });

  return (
    <Box data-social-auth sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {providers.map((provider) => {
        const colors = brand[provider];
        return (
          <ButtonBase
            key={provider}
            type="button"
            onClick={() => { void start(provider); }}
            disabled={disabled || pending !== null}
            aria-busy={pending === provider || undefined}
            data-provider={provider}
            sx={{
              width: '100%',
              minHeight: layoutTokens.button.md.minHeight,
              px: `${layoutTokens.button.md.paddingX}px`,
              gap: 2.5,
              borderRadius: `${layoutTokens.radius.md}px`,
              border: `1px solid ${colors.light.border}`,
              backgroundColor: colors.light.bg,
              color: colors.light.text,
              // Google asks for Roboto Medium; Apple for the system font.
              fontFamily: provider === 'google' ? 'Roboto, Inter, system-ui, sans-serif' : '-apple-system, BlinkMacSystemFont, Inter, system-ui, sans-serif',
              fontSize: 15,
              fontWeight: fontWeightTokens.medium,
              letterSpacing: 0,
              transition: `opacity ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`,
              '&.Mui-disabled': { opacity: 0.6 },
              '@media (hover: hover)': { '&:hover': { opacity: 0.9 } },
              '&:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: '2px' },
              [DARK]: { borderColor: colors.dark.border, backgroundColor: colors.dark.bg, color: colors.dark.text },
            }}
          >
            {provider === 'google' ? <GoogleMark /> : <AppleMark />}
            <span>{label(provider)}</span>
          </ButtonBase>
        );
      })}
      {error ? (
        <Box role="alert" sx={{ fontSize: 14, color: gateColors.error }}>{error}</Box>
      ) : null}
      {divider ? (
        <Box
          aria-hidden="true"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            mt: 1,
            fontSize: 14,
            color: gateColors.textSecondary,
            '&::before, &::after': { content: '""', flex: 1, borderTop: `1px solid ${gateColors.border}` },
          }}
        >
          {t('auth.social.orWithEmail', { defaultValue: 'or with email' })}
        </Box>
      ) : null}
    </Box>
  );
}
